import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AppState } from "../redux/reducer/rootReducer";
import {
  addGridSetting,
  useAddAppGridSettingMutation,
} from "../redux/reducer/features/App/AppGridSlice";
import { AppGridId } from "../models/app/AppGridModels";
import { useSendDealerEmailMutation } from "../redux/reducer/features/Dealer/DealerSettingsSlice";
import SnackBarPopup from "../compenents/app/SnackBarPopup";

const EARLY_ADOPTER_EMAIL = "brian.kristiansen@idealcomputersystems.com";

type ResponseChoice = "EarlyAdopter" | "Interested" | "NotInterested";

interface DealerContactInfo {
  name: string;
  email: string;
  phone: string;
  bestTimeToCall: string;
}

export interface CustomerLinkSetting {
  dealerId?: string;
  dmsDealerId?: string;
  responded: boolean;
  response?: ResponseChoice;
  contact?: DealerContactInfo;
  respondedOn?: string;
}

const SUBJECTS: Record<ResponseChoice, string> = {
  EarlyAdopter: "CustomerLink Early Adopter",
  Interested: "CustomerLink: Interested",
  NotInterested: "Not interested.",
};

const buildEmailBody = (
  choice: ResponseChoice,
  contact: DealerContactInfo,
  dealerId?: string,
  dmsDealerId?: string,
) => {
  const choiceLabel =
    choice === "EarlyAdopter"
      ? "Yes, I want to be an early adopter."
      : choice === "Interested"
        ? "I'm interested but don't want to be an Early Adopter."
        : "I'm not interested.";

  return (
    `CustomerLink response received.\n\n` +
    `Response: ${choiceLabel}\n\n` +
    `Dealer Information:\n` +
    `Name: ${contact.name}\n` +
    `Email: ${contact.email}\n` +
    `Direct Phone: ${contact.phone}\n` +
    `Best Time to Call: ${contact.bestTimeToCall}\n` +
    `Dealer Id: ${dealerId ?? ""}\n` +
    `DMS Dealer Id: ${dmsDealerId ?? ""}\n`
  );
};

const CustomerLink: React.FunctionComponent = () => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const { dealerId, dmsDealerId, name, email } = useSelector(
    (state: AppState) => state.authReducer,
  );
  const gridSettings = useSelector(
    (state: AppState) => state.AppGridReducer.setting,
  );
  const userId = useSelector((state: AppState) => state.authReducer.userId);

  const [saveSettingMutation] = useAddAppGridSettingMutation();
  const [sendDealerEmail] = useSendDealerEmailMutation();

  // ---- Existing saved response (per dealer) ----
  const existingResponse: CustomerLinkSetting | null = useMemo(() => {
    const row = gridSettings.find((g) => g.gridId === AppGridId.Customer_Link);
    if (!row) return null;

    return row.customerLinkSetting ?? null;
  }, [gridSettings]);

  const [hasResponded, setHasResponded] = useState<boolean>(
    !!existingResponse?.responded,
  );
  const [showInfoPage, setShowInfoPage] = useState<boolean>(
    !!existingResponse?.responded,
  );

  // Sync flags when grid settings load asynchronously (e.g. right after login).
  useEffect(() => {
    if (existingResponse?.responded) {
      setHasResponded(true);
      setShowInfoPage(true);
    }
  }, [existingResponse?.responded]);

  // ---- Contact info (prefilled from auth, prompted if missing) ----
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [pendingChoice, setPendingChoice] = useState<ResponseChoice | null>(
    null,
  );
  const [contact, setContact] = useState<DealerContactInfo>({
    name: existingResponse?.contact?.name ?? name ?? "",
    email: existingResponse?.contact?.email ?? email ?? "",
    phone: existingResponse?.contact?.phone ?? "",
    bestTimeToCall: existingResponse?.contact?.bestTimeToCall ?? "",
  });

  // ---- UI flow state ----
  const [thankYouOpen, setThankYouOpen] = useState(false);
  const [snack, setSnack] = useState<{
    open: boolean;
    severity: "success" | "error";
    message: string;
  }>({ open: false, severity: "success", message: "" });
  const [submitting, setSubmitting] = useState(false);

  // ---- Persist the dealer's response ----
  const persistResponse = (
    choice: ResponseChoice,
    contactInfo: DealerContactInfo,
  ) => {
    const payload: CustomerLinkSetting = {
      dealerId: dealerId,
      dmsDealerId: dmsDealerId,
      responded: true,
      response: choice,
      contact: contactInfo,
      respondedOn: new Date().toISOString(),
    };
    let data = {
      customerLinkSetting: payload,
    };
    const settingString = JSON.stringify(data);

    dispatch(
      addGridSetting({
        setting: {
          gridId: AppGridId.Customer_Link,
          customerLinkSetting: payload,
        },
      }),
    );

    return saveSettingMutation({
      data: {
        userId: userId ?? "",
        gridId: AppGridId.Customer_Link,
        setting: settingString,
      },
    }).unwrap();
  };

  // ---- Handle option click ----
  const handleChoice = (choice: ResponseChoice) => {
    const missing =
      !contact.name?.trim() || !contact.email?.trim() || !contact.phone?.trim();
    if (missing) {
      setPendingChoice(choice);
      setContactDialogOpen(true);
      return;
    }
    const finalContact: DealerContactInfo = {
      ...contact,
      bestTimeToCall: contact.bestTimeToCall?.trim() || "Not Selected",
    };
    submitChoice(choice, finalContact);
  };

  const submitChoice = (
    choice: ResponseChoice,
    contactInfo: DealerContactInfo,
  ) => {
    // Fire-and-forget: don't make the dealer wait for these.
    persistResponse(choice, contactInfo).catch(() => undefined);
    sendDealerEmail({
      FromName: contactInfo.name,
      EmailTo: EARLY_ADOPTER_EMAIL,
      Subject: SUBJECTS[choice],
      Body: buildEmailBody(choice, contactInfo, dealerId, dmsDealerId),
    })
      .unwrap()
      .catch(() => undefined);

    // Update UI immediately for all three cases.
    setHasResponded(true);
    setShowInfoPage(true);

    setThankYouOpen(true);
    setTimeout(() => {
      setThankYouOpen(false);
      navigate("/");
    }, 5000);
  };

  const handleContactDialogSubmit = () => {
    if (
      !contact.name?.trim() ||
      !contact.email?.trim() ||
      !contact.phone?.trim() ||
      !pendingChoice
    ) {
      setSnack({
        open: true,
        severity: "error",
        message: "Please fill in Name, Email and Direct Phone.",
      });
      return;
    }
    setContactDialogOpen(false);
    const choice = pendingChoice;
    setPendingChoice(null);
    const finalContact: DealerContactInfo = {
      ...contact,
      bestTimeToCall: contact.bestTimeToCall?.trim() || "Not Selected",
    };
    submitChoice(choice, finalContact);
  };

  // ---------- CustomerLink information page (after a choice was made) ----------
  const InfoPage = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        p: 3,
        overflowY: "auto",
      }}
    >
      <Card
        sx={{
          maxWidth: 760,
          width: "100%",
          borderRadius: 3,
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        }}
      >
        <CardContent>
          <Typography variant="h5" fontWeight={700} mb={2}>
            CustomerLink
          </Typography>
          <Typography variant="body1" mb={2}>
            We're excited to invite you to become an early adopter of a new
            customer portal designed to help dealers streamline customer
            interactions, accelerate payments, and drive self-service
            engagement.
          </Typography>
          <Typography variant="body1" fontWeight={600} mt={2} mb={1}>
            With CustomerLink, your customers can:
          </Typography>
          <Box component="ul" sx={{ pl: 3, m: 0 }}>
            <li>View purchase history and customer statements</li>
            <li>Make secure payments via credit card or ACH</li>
            <li>Order products or request quotes online</li>
            <li>Schedule service appointments</li>
            <li>Manage their users and account information</li>
          </Box>
          <Typography variant="body1" mt={2}>
            As an early adopter, you'll get access before general availability
            and work closely with our team as we continue to enhance
            CustomerLink.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );

  // ---------- Invite (no subscription, no response yet) ----------
  const InvitePage = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        p: 3,
        overflowY: "auto",
      }}
    >
      <Card
        sx={{
          maxWidth: 760,
          width: "100%",
          borderRadius: 3,
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        }}
      >
        <CardContent>
          <Typography variant="h5" fontWeight={700} mb={2}>
            CustomerLink — Early Adopter Invitation
          </Typography>
          <Typography variant="body1" mb={2}>
            We're excited to invite you to become an early adopter of a new
            customer portal designed to help dealers streamline customer
            interactions, accelerate payments, and drive self-service
            engagement.
          </Typography>
          <Typography variant="body1" fontWeight={600} mt={2} mb={1}>
            With CustomerLink, your customers can:
          </Typography>
          <Box component="ul" sx={{ pl: 3, m: 0 }}>
            <li>View purchase history and customer statements</li>
            <li>Make secure payments via credit card or ACH</li>
            <li>Order products or request quotes online</li>
            <li>Schedule service appointments</li>
            <li>Manage their users and account information</li>
          </Box>
          <Typography variant="body1" mt={2} mb={3}>
            As an early adopter, you'll get access before general availability
            and work closely with our team as we continue to enhance
            CustomerLink.
          </Typography>

          <Typography variant="h6" fontWeight={600} mb={2}>
            Interested in joining the early adopter program?
          </Typography>

          <Stack spacing={1.5}>
            <Button
              variant="contained"
              color="success"
              disabled={submitting}
              onClick={() => handleChoice("EarlyAdopter")}
              startIcon={
                submitting ? (
                  <CircularProgress size={18} color="inherit" />
                ) : null
              }
            >
              Yes, I want to be an early adopter
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={submitting}
              onClick={() => handleChoice("Interested")}
            >
              I'm interested but don't want to be an Early Adopter
            </Button>
            <Button
              variant="outlined"
              color="inherit"
              disabled={submitting}
              onClick={() => handleChoice("NotInterested")}
            >
              I'm not interested
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );

  return (
    <Container
      maxWidth={false}
      sx={{ height: "calc(100vh - 80px)", p: 0, overflow: "hidden" }}
    >
      {showInfoPage || hasResponded ? InfoPage : InvitePage}

      {/* Contact info dialog */}
      <Dialog
        open={contactDialogOpen}
        onClose={() => setContactDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Confirm your contact information</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Name"
              value={contact.name}
              onChange={(e) => setContact({ ...contact, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Email"
              type="email"
              value={contact.email}
              onChange={(e) =>
                setContact({ ...contact, email: e.target.value })
              }
              fullWidth
              required
            />
            <TextField
              label="Direct Phone"
              value={contact.phone}
              onChange={(e) =>
                setContact({ ...contact, phone: e.target.value })
              }
              fullWidth
              required
            />
            <TextField
              label="Best Time to Call (optional)"
              select
              value={contact.bestTimeToCall}
              onChange={(e) =>
                setContact({ ...contact, bestTimeToCall: e.target.value })
              }
              fullWidth
            >
              <MenuItem value="">Not Selected</MenuItem>
              <MenuItem value="Morning">Morning (8am - 12pm)</MenuItem>
              <MenuItem value="Afternoon">Afternoon (12pm - 5pm)</MenuItem>
              <MenuItem value="Evening">Evening (5pm - 8pm)</MenuItem>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setContactDialogOpen(false)}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleContactDialogSubmit}
            disabled={submitting}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Thank you dialog (5s auto-redirect) */}
      <Dialog open={thankYouOpen} fullWidth maxWidth="xs">
        <DialogContent>
          <Stack alignItems="center" spacing={1.5} sx={{ py: 2 }}>
            <Typography variant="h6" textAlign="center">
              Thank you for your interest!
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
            >
              We'll be contacting you soon. Redirecting to your dashboard...
            </Typography>
            <CircularProgress size={28} />
          </Stack>
        </DialogContent>
      </Dialog>
      <SnackBarPopup
        open={snack.open}
        severity={snack.severity}
        message={snack.message}
        onClose={() => setSnack({ ...snack, open: false })}
        autoHideDuration={4000}
      />
    </Container>
  );
};

export default CustomerLink;
